import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Quote, Star, ChevronLeft, ChevronRight } from 'lucide-react';
import './Testimonials.css';

const testimonials = [
  {
    id: 1,
    name: 'Rajesh Sharma',
    role: 'CTO',
    company: 'FinanceFlow India',
    avatar: 'RS',
    rating: 5,
    accentColor: '#06b6d4',
    text: `Wayug Infotech transformed our core banking infrastructure in under six months — a timeline we thought impossible. Their team's depth of technical expertise and relentless attention to quality gave us a platform that handles 2× our peak load without breaking a sweat.`,
  },
  {
    id: 2,
    name: 'Priya Patel',
    role: 'CEO',
    company: 'MediLink Solutions',
    avatar: 'PP',
    rating: 5,
    accentColor: '#ef4444',
    text: `From day one the Wayug team understood the unique compliance constraints of healthcare IT. They delivered a HIPAA-compliant telemedicine platform that our clinicians actually love using — patient adoption exceeded our targets by 80% in the first quarter.`,
  },
  {
    id: 3,
    name: 'Arun Kumar',
    role: 'Director of IT',
    company: 'GovTech UP',
    avatar: 'AK',
    rating: 5,
    accentColor: '#7c3aed',
    text: `Rolling out e-governance at scale across multiple districts demands a partner who understands both technology and the realities of public-sector deployment. Wayug delivered on every milestone and provided outstanding post-launch support — a true government-grade partnership.`,
  },
  {
    id: 4,
    name: 'Vikram Singh',
    role: 'Founder',
    company: 'RetailMax',
    avatar: 'VS',
    rating: 5,
    accentColor: '#10b981',
    text: `Our commerce platform now handles flash-sale spikes of 50,000 concurrent users without a single dropped request. Wayug's cloud-native architecture and proactive monitoring team have made downtime a thing of the past for RetailMax.`,
  },
];

const slideVariants = {
  enter: (dir) => ({
    x: dir > 0 ? 60 : -60,
    opacity: 0,
  }),
  center: {
    x: 0,
    opacity: 1,
    transition: { duration: 0.45, ease: [0.22, 1, 0.36, 1] },
  },
  exit: (dir) => ({
    x: dir > 0 ? -60 : 60,
    opacity: 0,
    transition: { duration: 0.3, ease: [0.22, 1, 0.36, 1] },
  }),
};

export default function Testimonials() {
  const [activeIndex, setActiveIndex] = useState(0);
  const [direction, setDirection] = useState(1);

  const goTo = useCallback(
    (index) => {
      const next = (index + testimonials.length) % testimonials.length;
      setDirection(next > activeIndex ? 1 : -1);
      setActiveIndex(next);
    },
    [activeIndex]
  );

  const prev = () => goTo(activeIndex - 1);
  const next = useCallback(() => goTo(activeIndex + 1), [activeIndex, goTo]);

  /* Auto-advance every 5 s */
  useEffect(() => {
    const timer = setInterval(next, 5000);
    return () => clearInterval(timer);
  }, [next]);

  const active = testimonials[activeIndex];

  return (
    <section className="testimonials" aria-label="Client testimonials">
      <div className="container">
        {/* ── Section Header ── */}
        <div className="section-header">
          <span className="section-label">CLIENT STORIES</span>
          <h2 className="section-title">
            Trusted by <span className="gradient-text">Industry Leaders</span>
          </h2>
          <p className="section-subtitle">
            Real outcomes from real partners — across industries, geographies, and scale.
          </p>
        </div>

        {/* ── Testimonial Wrapper ── */}
        <div className="testimonials-wrapper">
          <AnimatePresence mode="wait" custom={direction}>
            <motion.div
              key={active.id}
              className="testimonial-card"
              custom={direction}
              variants={slideVariants}
              initial="enter"
              animate="center"
              exit="exit"
            >
              {/* Quote icon */}
              <div className="quote-icon" aria-hidden="true">
                <Quote size={48} strokeWidth={1.25} />
              </div>

              {/* Text */}
              <p className="testimonial-text">"{active.text}"</p>

              {/* Author */}
              <div className="testimonial-author">
                <div
                  className="author-avatar"
                  style={{ background: `linear-gradient(135deg, ${active.accentColor}, #7c3aed)` }}
                  aria-label={`${active.name} avatar`}
                >
                  {active.avatar}
                </div>

                <div className="author-info">
                  <strong className="author-name">{active.name}</strong>
                  <span className="author-role">
                    {active.role} · {active.company}
                  </span>
                </div>

                {/* Star rating */}
                <div className="star-rating" aria-label={`${active.rating} out of 5 stars`}>
                  {Array.from({ length: active.rating }).map((_, i) => (
                    <Star key={i} size={15} fill="#f59e0b" color="#f59e0b" />
                  ))}
                </div>
              </div>
            </motion.div>
          </AnimatePresence>

          {/* ── Controls ── */}
          <div className="testimonial-controls">
            <button
              className="control-btn"
              onClick={prev}
              aria-label="Previous testimonial"
            >
              <ChevronLeft size={18} />
            </button>

            <div className="dot-indicators" role="tablist" aria-label="Testimonial navigation">
              {testimonials.map((_, i) => (
                <button
                  key={i}
                  role="tab"
                  aria-selected={i === activeIndex}
                  aria-label={`Go to testimonial ${i + 1}`}
                  className={`dot${i === activeIndex ? ' dot--active' : ''}`}
                  onClick={() => goTo(i)}
                />
              ))}
            </div>

            <button
              className="control-btn"
              onClick={next}
              aria-label="Next testimonial"
            >
              <ChevronRight size={18} />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}
